import {
    filterOrderThunk,
    createNewToteThunk,
    closeToteThunk,
    shipToteThunk,
    approveToteThunk,
    getOrderByIdThunk,
    exportOrdersThunk,
    cancelOrderThunk,
} from '@redux/features/order.slice';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import { RequestStatus } from '@core/enums/request-status.enum';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import Pagination from 'Common/Pagination';
import React, { FC, useEffect, useState, useCallback } from 'react';
import ViewOrderDetailsModal from './ViewOrderDetailsModal';
import ScanProductModal from './ScanProductModal';
import ApproveToteModal from './ApproveToteModal';
import ShipToteModal from './ShipToteModal';
import ToteIssueModal from './ToteIssueModal';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import DebouncedInput from 'Shared/DebouncedInput';
import ExportButton from 'Shared/ExportButton';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';

const Orders: FC = () => {
    const [page, setPage] = useState<number>(1);
    const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
    const [showScanModal, setShowScanModal] = useState<boolean>(false);
    const [showCloseToteModal, setShowCloseToteModal] = useState<boolean>(false);
    const [showShipToteModal, setShowShipToteModal] = useState<boolean>(false);
    const [showApproveToteModal, setShowApproveToteModal] = useState<boolean>(false);
    const [showCancelOrderModal, setShowCancelOrderModal] = useState<boolean>(false);
    const [showIssueModal, setShowIssueModal] = useState<boolean>(false);
    const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
    const [selectedToteId, setSelectedToteId] = useState<number | null>(null);
    const [orderIdToCancel, setOrderIdToCancel] = useState<number | null>(null);
    const [selectedOrder, setSelectedOrder] = useState<any>(null);
    const [selectedOrderForIssue, setSelectedOrderForIssue] = useState<any>(null);
    const [barCode, setBarCode] = useState<string>('');

    // Filter states
    const [filterOrderId, setFilterOrderId] = useState<string>('');
    const [filterStatus, setFilterStatus] = useState<string>('');
    const [filterMinAmount, setFilterMinAmount] = useState<string>('');
    const [filterMaxAmount, setFilterMaxAmount] = useState<string>('');
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [filterIsPaid, setFilterIsPaid] = useState<string>('all'); // 'all', 'paid', 'notPaid'
    const [filterSortBy, setFilterSortBy] = useState<string>('orderDate'); // default: orderDate
    const [filterSortOrder, setFilterSortOrder] = useState<string>('DESC'); // default: DESC

    // -------------------------------------------------------------
    const dispatch = useAppDispatch();
    // -------------------------------------------------------------
    const {
        orders,
        order,
        filterOrderLoading,
        exportOrdersLoading,
        createNewToteLoading,
        closeToteLoading,
        shipToteLoading,
        approveToteLoading,
        getOrderByIdLoading,
        cancelOrderLoading,
        pagination,
    } = useAppSelector((state: RootState) => state.orderState);
    // -------------------------------------------------------------
    const changePage = (p: string) => {
        setPage(Number(p));
    };

    // Helper function to format date to YYYY-MM-DDTHH:MM:SS format (for filter and export)
    const formatDateForAPI = (date: Date, isEndDate: boolean = false): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const time = isEndDate ? '23:59:59' : '00:00:00';
        return `${year}-${month}-${day}T${time}`;
    };

    // Helper function to format date to YYYY-MM-DDTHH:MM:SS format (for export)
    const formatDateForExport = (date: Date, isEndDate: boolean = false): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const time = isEndDate ? '23:59:59' : '00:00:00';
        return `${year}-${month}-${day}T${time}`;
    };

    // Handle date range change - only send when both dates are selected
    const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
        setDateRange(dates);
        const [start, end] = dates;
        // Only update string dates when both start and end are selected
        if (start && end) {
            setStartDate(formatDateForAPI(start, false));
            setEndDate(formatDateForAPI(end, true));
        } else if (!start && !end) {
            // Clear both when range is cleared
            setStartDate('');
            setEndDate('');
        }
        // If only start is selected, don't update the string dates yet
    };

    // -------------------------------------------------------------
    useEffect(() => {
        const filterParams: any = {
            pageSize: 10,
            pageNumber: page - 1,
        };

        // Add orderId filter if provided
        if (filterOrderId && filterOrderId.trim() !== '') {
            filterParams.orderId = filterOrderId.trim();
        }

        // Add status filter if provided
        if (filterStatus && filterStatus.trim() !== '') {
            filterParams.status = filterStatus.trim();
        }

        // Add minAmount filter if provided
        if (filterMinAmount && filterMinAmount.trim() !== '') {
            const minAmount = parseFloat(filterMinAmount);
            if (!isNaN(minAmount)) {
                filterParams.minAmount = minAmount;
            }
        }

        // Add maxAmount filter if provided
        if (filterMaxAmount && filterMaxAmount.trim() !== '') {
            const maxAmount = parseFloat(filterMaxAmount);
            if (!isNaN(maxAmount)) {
                filterParams.maxAmount = maxAmount;
            }
        }

        // Add dateFrom filter if provided
        if (startDate && startDate.trim() !== '') {
            filterParams.dateFrom = startDate;
        }

        // Add dateTo filter if provided
        if (endDate && endDate.trim() !== '') {
            filterParams.dateTo = endDate;
        }

        // Add isPaid filter if provided
        if (filterIsPaid && filterIsPaid !== 'all') {
            filterParams.isPaid = filterIsPaid === 'paid';
        }

        // Add sortBy and sortOrder filters (always include with defaults)
        filterParams.sortBy = filterSortBy || 'orderDate';
        filterParams.sortOrder = filterSortOrder || 'DESC';

        dispatch(filterOrderThunk(filterParams));
    }, [page, filterOrderId, filterStatus, filterMinAmount, filterMaxAmount, startDate, endDate, filterIsPaid, filterSortBy, filterSortOrder, dispatch]);
    // -------------------------------------------------------------

    const handleViewOrderDetails = (orderId: number) => {
        setSelectedOrderId(orderId);
        setShowDetailsModal(true);
    };

    const handleCloseDetailsModal = () => {
        setShowDetailsModal(false);
        setSelectedOrderId(null);
        setSelectedOrder(null);
    };

    // Function to refresh order details modal
    const handleRefreshOrderDetails = () => {
        if (selectedOrderId && showDetailsModal) {
            // Immediately refresh order data
            dispatch(getOrderByIdThunk(selectedOrderId));
        }
    };

    // Update selectedOrder when order data changes (for real-time updates in ScanProductModal)
    useEffect(() => {
        if (order && showScanModal && selectedOrderId && order.order?.id === selectedOrderId) {
            // Update selectedOrder with fresh data from Redux
            const updatedOrder = {
                ...order,
                reservedProducts: order.reservedProducts || [],
            };
            setSelectedOrder(updatedOrder);
        }
    }, [order, showScanModal, selectedOrderId]);

    const handleCreateNewTote = (orderId: number) => {
        if (createNewToteLoading) return; // Prevent multiple calls
        dispatch(createNewToteThunk(orderId)).then((res: any) => {
            if (res.meta.requestStatus === 'fulfilled') {
                // Refresh orders list
                dispatch(filterOrderThunk({ pageSize: 10, pageNumber: page - 1 }));
                // Refresh order details modal if it's open
                if (showDetailsModal && selectedOrderId) {
                    // Trigger refresh by dispatching getOrderByIdThunk
                    setTimeout(() => {
                        dispatch(getOrderByIdThunk(selectedOrderId));
                    }, 500);
                }
            }
        });
    };

    const handleScanProduct = (order: any) => {
        // Use the selected tote from the order if provided, otherwise find the first open tote
        const selectedTote = order.selectedTote;
        const totes = order.totes || order.order?.totes || [];

        let targetTote;
        if (selectedTote) {
            targetTote = selectedTote;
        } else {
            // Fallback: find the first open tote
            targetTote = totes.find((tote: any) => tote.status === 'IN_PROGRESS');
        }

        if (targetTote) {
            // Include reservedProducts in the order data passed to ScanProductModal
            const orderWithReservedProducts = {
                ...order,
                reservedProducts: order.reservedProducts || order.order?.reservedProducts || [],
            };
            setSelectedOrder(orderWithReservedProducts);
            setBarCode(targetTote.toteBarcode);
            console.log(targetTote.toteBarcode);
            setSelectedToteId(targetTote.id?.toString() || targetTote.id);
            setShowScanModal(true);
        }
    };

    const handleCloseScanModal = () => {
        setShowScanModal(false);
        setSelectedOrder(null);
        setSelectedToteId(null);
        // Refresh orders list
        dispatch(filterOrderThunk({ pageSize: 10, pageNumber: page - 1 }));
        // Refresh order details modal if it's open
        if (showDetailsModal && selectedOrderId) {
            // Immediately refresh order data
            dispatch(getOrderByIdThunk(selectedOrderId));
        }
    };

    // Function to update selectedOrder with fresh data after scanning
    const handleUpdateScanOrder = (updatedOrder: any) => {
        if (showScanModal && selectedOrder) {
            setSelectedOrder(updatedOrder);
        }
    };

    const handleCloseTote = (toteId: number) => {
        setSelectedToteId(toteId);
        setShowCloseToteModal(true);
    };

    const handleConfirmCloseTote = () => {
        if (selectedToteId && !closeToteLoading) {
            dispatch(closeToteThunk(selectedToteId)).then((res: any) => {
                if (res.meta.requestStatus === 'fulfilled') {
                    setShowCloseToteModal(false);
                    setSelectedToteId(null);
                    dispatch(filterOrderThunk({ pageSize: 10, pageNumber: page - 1 }));
                    // Refresh order details modal if it's open
                    if (showDetailsModal && selectedOrderId) {
                        // Trigger refresh by dispatching getOrderByIdThunk
                        setTimeout(() => {
                            dispatch(getOrderByIdThunk(selectedOrderId));
                        }, 500);
                    }
                }
            });
        }
    };

    const handleShipTote = (toteId: number) => {
        setSelectedToteId(toteId);
        setShowShipToteModal(true);
    };

    const handleShipToteComplete = () => {
        setSelectedToteId(null);
        dispatch(filterOrderThunk({ pageSize: 10, pageNumber: page - 1 }));
        // Refresh order details modal if it's open
        if (showDetailsModal && selectedOrderId) {
            // Trigger refresh by dispatching getOrderByIdThunk
            setTimeout(() => {
                dispatch(getOrderByIdThunk(selectedOrderId));
            }, 500);
        }
    };

    const handleApproveTote = (toteId: string, toteBarcode: string) => {
        setSelectedToteId(Number(toteId));
        setShowApproveToteModal(true);
    };

    const handleApproveToteComplete = () => {
        setSelectedToteId(null);
        dispatch(filterOrderThunk({ pageSize: 10, pageNumber: page - 1 }));
        // Refresh order details modal if it's open
        if (showDetailsModal && selectedOrderId) {
            // Trigger refresh by dispatching getOrderByIdThunk
            setTimeout(() => {
                dispatch(getOrderByIdThunk(selectedOrderId));
            }, 500);
        }
    };

    const handleCancelOrder = (orderId: number) => {
        setOrderIdToCancel(orderId);
        setShowCancelOrderModal(true);
    };

    const handleConfirmCancelOrder = () => {
        if (orderIdToCancel && !cancelOrderLoading) {
            dispatch(cancelOrderThunk(orderIdToCancel)).then((res: any) => {
                if (res.meta.requestStatus === RequestStatus.FULFILLED) {
                    setShowCancelOrderModal(false);
                    setOrderIdToCancel(null);
                    // Refresh orders list
                    dispatch(filterOrderThunk({ pageSize: 10, pageNumber: page - 1 }));
                    // Refresh order details modal if it's open
                    if (showDetailsModal && selectedOrderId) {
                        setTimeout(() => {
                            dispatch(getOrderByIdThunk(selectedOrderId));
                        }, 500);
                    }
                }
            });
        }
    };

    // Helper function to check if order status includes PENDING
    const isPendingStatus = (status: string) => {
        return status?.includes('PENDING') || status === 'PENDING';
    };

    // Helper function to check if order has open tote
    const hasOpenTote = (order: any) => {
        return order.totes?.some((tote: any) => tote.status === 'IN_PROGRESS');
    };

    // Helper function to check if order has tote with issue
    const hasToteIssue = (order: any) => {
        const totes = order.totes || [];
        return totes.some((tote: any) => tote.issueNote || tote.issueReportedAt);
    };

    // Handle view issue
    const handleViewIssue = (order: any) => {
        setSelectedOrderForIssue(order);
        setShowIssueModal(true);
    };

    // Handle close issue modal
    const handleCloseIssueModal = () => {
        setShowIssueModal(false);
        setSelectedOrderForIssue(null);
    };

    // Helper function to format date
    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    // Helper function to format status
    const formatStatus = (status: string | null | undefined) => {
        if (!status) return '-';
        return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    };

    // Handle export
    const handleExport = () => {
        if (exportOrdersLoading) return; // Prevent multiple calls
        // Format dates with time component for export
        const exportStartDate = dateRange[0] ? formatDateForExport(dateRange[0], false) : '';
        const exportEndDate = dateRange[1] ? formatDateForExport(dateRange[1], true) : '';

        dispatch(exportOrdersThunk({ startDate: exportStartDate, endDate: exportEndDate })).then((res: any) => {
            if (res.meta.requestStatus === RequestStatus.FULFILLED) {
                const downloadUrl = res.payload?.exportRequest?.downloadUrl || res.payload?.exportRequest?.filePath;
                console.log(res);
                if (downloadUrl) {
                    const a = document.createElement('a');
                    a.href = downloadUrl;
                    a.download = 'Orders.csv';
                    a.target = '_blank';
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                } else {
                    toast.error('Download URL not found in response', { autoClose: 2000 });
                }
            }
        });
    };

    // Memoized search functions to prevent unnecessary re-renders
    const handleOrderIdSearch = useCallback(
        (v: string) => {
            if (v !== filterOrderId) {
                setFilterOrderId(v);
                setPage(1);
            }
        },
        [filterOrderId]
    );

    const handleMinAmountSearch = useCallback(
        (v: string) => {
            if (v !== filterMinAmount) {
                setFilterMinAmount(v);
                setPage(1);
            }
        },
        [filterMinAmount]
    );

    const handleMaxAmountSearch = useCallback(
        (v: string) => {
            if (v !== filterMaxAmount) {
                setFilterMaxAmount(v);
                setPage(1);
            }
        },
        [filterMaxAmount]
    );

    const handleStatusChange = useCallback(
        (v: string) => {
            if (v !== filterStatus) {
                setFilterStatus(v);
                setPage(1);
            }
        },
        [filterStatus]
    );

    const handleIsPaidChange = useCallback(
        (v: string) => {
            if (v !== filterIsPaid) {
                setFilterIsPaid(v);
                setPage(1);
            }
        },
        [filterIsPaid]
    );

    const handleSortByChange = useCallback(
        (v: string) => {
            if (v !== filterSortBy) {
                setFilterSortBy(v);
                setPage(1);
            }
        },
        [filterSortBy]
    );

    const handleSortOrderChange = useCallback(
        (v: string) => {
            if (v !== filterSortOrder) {
                setFilterSortOrder(v);
                setPage(1);
            }
        },
        [filterSortOrder]
    );

    // -------------------------------------------------------------

    return (
        <>
            <ViewOrderDetailsModal
                show={showDetailsModal}
                handleClose={handleCloseDetailsModal}
                orderId={selectedOrderId || 0}
                onCreateTote={handleCreateNewTote}
                onScanProduct={handleScanProduct}
                onCloseTote={handleCloseTote}
                onShipTote={handleShipTote}
                onApproveTote={handleApproveTote}
                onActionComplete={handleRefreshOrderDetails}
            />
            <ScanProductModal
                show={showScanModal}
                handleClose={handleCloseScanModal}
                toteId={selectedToteId?.toString() || ''}
                order={selectedOrder}
                closeViewOrderDetailsModal={() => setShowDetailsModal(false)}
                onScanComplete={handleRefreshOrderDetails}
            />
            <ConfirmationModal
                show={showCloseToteModal}
                handleClose={() => {
                    setShowCloseToteModal(false);
                    setSelectedToteId(null);
                }}
                onConfirm={handleConfirmCloseTote}
                title="Close Tote"
                message="Are you sure you want to close this tote?"
                confirmButtonText="Close"
                cancelButtonText="Cancel"
                isLoading={closeToteLoading}
            />
            <ShipToteModal
                show={showShipToteModal}
                handleClose={() => {
                    setShowShipToteModal(false);
                    setSelectedToteId(null);
                }}
                toteId={selectedToteId}
                onShipComplete={handleShipToteComplete}
            />
            <ConfirmationModal
                show={showCancelOrderModal}
                handleClose={() => {
                    setShowCancelOrderModal(false);
                    setOrderIdToCancel(null);
                }}
                onConfirm={handleConfirmCancelOrder}
                title="Cancel Order"
                message="Are you sure you want to cancel this order? This action cannot be undone."
                confirmButtonText="Cancel Order"
                cancelButtonText="No, Keep Order"
                isLoading={cancelOrderLoading}
            />
            <ApproveToteModal
                show={showApproveToteModal}
                handleClose={() => {
                    setShowApproveToteModal(false);
                    setSelectedToteId(null);
                }}
                onApproveComplete={handleApproveToteComplete}
            />
            <ToteIssueModal
                show={showIssueModal}
                handleClose={handleCloseIssueModal}
                order={selectedOrderForIssue}
            />
            <BreadCrumb pageTitle={`Orders`} />
            <section className="py-5">
                <div className=" p-3 flex flex-col gap-2 bg-[#fff]">
                    <div className="flex w-full items-center justify-between">
                        <h3 className="font-extrabold text-[#bd902e]">Orders List</h3>
                    </div>
                    {/* Filters Row */}
                    <div className="flex flex-wrap gap-4">
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Order ID Or User ID</label>
                            <DebouncedInput
                                placeholder="Search by order ID Or user id"
                                searchValue={filterOrderId}
                                searchFn={handleOrderIdSearch}
                            />
                        </div>
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Min Amount</label>
                            <DebouncedInput
                                placeholder="Enter minimum amount"
                                searchValue={filterMinAmount}
                                searchFn={handleMinAmountSearch}
                            />
                        </div>
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Max Amount</label>
                            <DebouncedInput
                                placeholder="Enter maximum amount"
                                searchValue={filterMaxAmount}
                                searchFn={handleMaxAmountSearch}
                            />
                        </div>
                        <div className="flex-1 min-w-[300px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Date Range</label>
                            <DatePicker
                                selected={dateRange[0]}
                                onChange={handleDateRangeChange}
                                startDate={dateRange[0]}
                                endDate={dateRange[1]}
                                selectsRange
                                isClearable
                                placeholderText="Select date range"
                                className="form-input border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400 w-full transition-all duration-200"
                                dateFormat="yyyy-MM-dd"
                            />
                        </div>
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Status</label>
                            <select
                                value={filterStatus}
                                onChange={(e) => handleStatusChange(e.target.value)}
                                className="form-select border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400 w-full transition-all duration-200"
                            >
                                <option value="">All Statuses</option>
                                <option value="PENDING">Pending</option>
                                <option value="PENDING_BACKORDER">Pending Backorder</option>
                                <option value="PROCESSING">Processing</option>
                                <option value="PENDING_PHARMACIST_APPROVAL">Pending Pharmacist Approval</option>
                                <option value="SHIPPED">Shipped</option>
                                <option value="PARTIAL_SHIPPED">Partial Shipped</option>
                                <option value="DELIVERED">Delivered</option>
                                <option value="PARTIAL_DELIVERED">Partial Delivered</option>
                                <option value="CANCELLED">Cancelled</option>
                                <option value="REFUNDED">Refunded</option>
                                <option value="READY_FOR_SHIPMENT">Ready For Shipment</option>
                                <option value="FAILED">Failed</option>
                            </select>
                        </div>
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Payment Status</label>
                            <select
                                value={filterIsPaid}
                                onChange={(e) => handleIsPaidChange(e.target.value)}
                                className="form-select border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400 w-full transition-all duration-200"
                            >
                                <option value="all">All</option>
                                <option value="paid">Paid</option>
                                <option value="notPaid">Not Paid</option>
                            </select>
                        </div>
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Sort By</label>
                            <select
                                value={filterSortBy}
                                onChange={(e) => handleSortByChange(e.target.value)}
                                className="form-select border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400 w-full transition-all duration-200"
                            >
                                <option value="orderDate">Order Date</option>
                                <option value="id">ID</option>
                                <option value="totalAmount">Total Amount</option>
                                <option value="orderStatus">Status</option>
                            </select>
                        </div>
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Sort Order</label>
                            <select
                                value={filterSortOrder}
                                onChange={(e) => handleSortOrderChange(e.target.value)}
                                className="form-select border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400 w-full transition-all duration-200"
                            >
                                <option value="DESC">Descending</option>
                                <option value="ASC">Ascending</option>
                            </select>
                        </div>
                        <div className="ml-auto flex items-end">
                            <ExportButton onExport={handleExport} disabled={exportOrdersLoading} />
                        </div>
                    </div>
                    {filterOrderLoading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div>
                                <div className="overflow-x-auto">
                                    <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                        <thead className="ltr:text-left rtl:text-right">
                                            <tr
                                                className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                            >
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Order Date</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Status</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Total Amount</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Final Amount</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Is Paid</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Payment Method
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Recipient Name
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Details</th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={filterOrderLoading} itemCount={orders?.length ?? 0} animationKey={`orders-${page}`}>
                                            {orders?.map((order: any) => (
                                                <AnimatedTableRow
                                                    rowKey={order?.id}
                                                    detailRecord={order}
                                                    className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                                >
                                                    <td className="px-3.5 py-2.5">{order?.id || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{formatDate(order?.orderDate)}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        <span
                                                            className={`px-2 py-1 rounded text-xs font-semibold ${
                                                                order?.orderStatus === 'PENDING' ||
                                                                order?.orderStatus?.includes('PENDING')
                                                                    ? 'bg-yellow-100 text-yellow-800'
                                                                    : order?.orderStatus === 'SHIPPED' ||
                                                                        order?.orderStatus === 'DELIVERED'
                                                                      ? 'bg-green-100 text-green-800'
                                                                      : order?.orderStatus === 'CANCELLED' ||
                                                                          order?.orderStatus === 'FAILED'
                                                                        ? 'bg-red-100 text-red-800'
                                                                        : 'bg-blue-100 text-blue-800'
                                                            }`}
                                                        >
                                                            {formatStatus(order?.orderStatus)}
                                                        </span>
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {order?.totalAmount !== null && order?.totalAmount !== undefined
                                                            ? `$${order.totalAmount.toFixed(2)}`
                                                            : `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {order?.finalAmount !== null && order?.finalAmount !== undefined
                                                            ? `$${order.finalAmount.toFixed(2)}`
                                                            : `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        <span
                                                            className={`px-2 py-1 rounded text-xs font-semibold ${
                                                                order?.isPaid
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : 'bg-red-100 text-red-800'
                                                            }`}
                                                        >
                                                            {order?.isPaid ? 'Paid' : 'Not Paid'}
                                                        </span>
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {formatStatus(order?.paymentMethod) || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {order?.shippingRecipientName || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        <div className="flex flex-col justify-center items-center gap-2.5">
                                                            <button
                                                                className="mx-auto group flex w-full px-2 text-xs font-semibold py-1 items-center justify-center
                                                        rounded-md border border-yellow-600
                                                        bg-white transition-all duration-300 ease-linear hover:scale-105"
                                                                onClick={() => {
                                                                    handleViewOrderDetails(order?.id);
                                                                }}
                                                            >
                                                                View Order Details
                                                            </button>
                                                            {hasToteIssue(order) && (
                                                                <button
                                                                    className="mx-auto group flex w-full px-2 text-xs font-semibold py-1 items-center justify-center
                                                            rounded-md border border-orange-600
                                                            bg-orange-50 text-orange-700 transition-all duration-300 ease-linear hover:scale-105 hover:bg-orange-100"
                                                                    onClick={() => {
                                                                        handleViewIssue(order);
                                                                    }}
                                                                >
                                                                    View Issue
                                                                </button>
                                                            )}
                                                            <button
                                                                className="mx-auto group flex w-full px-2 text-xs font-semibold py-1 items-center justify-center
                                                        rounded-md border border-red-600
                                                        bg-white transition-all duration-300 ease-linear hover:scale-105 hover:bg-red-50"
                                                                onClick={() => {
                                                                    handleCancelOrder(order?.id);
                                                                }}
                                                            >
                                                                Cancel Order
                                                            </button>
                                                        </div>
                                                    </td>
                                                </AnimatedTableRow>
                                            ))}
                                        </AnimatedTableBody>
                                    </AnimatedTable>
                                </div>
                                <Pagination
                                    perPageData={pagination?.pageSize || 10}
                                    total={pagination?.totalElements || 0}
                                    currentPage={page}
                                    changePage={changePage}
                                    length={orders?.length || 0}
                                />
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default Orders;
